import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Zap, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  { id: 'starter', name: 'Starter', price: '0', desc: '14 Tage kostenlos', users: 3, storage: '1 GB', features: ['Einsatzplanung', 'Zeiterfassung', 'Projekt-Chat'] },
  { id: 'professional', name: 'Professional', price: '49', desc: 'pro Monat', users: 25, storage: '5 GB', features: ['Alle Funktionen', 'PDF mit Logo', 'Berechtigungen', 'Materialkatalog'] },
  { id: 'enterprise', name: 'Enterprise', price: '129', desc: 'pro Monat', users: 999, storage: '50 GB', features: ['Unbegrenzt', 'API-Zugang', 'Dedicated Support', 'On-Premise'] },
];

export default function RegisterCompany() {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [form, setForm] = useState({ company_name: '', manager_name: '', email: '', address: '', zip_code: '', city: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleRegister = async () => {
    if (!form.company_name || !form.manager_name || !form.email) {
      toast.error('Bitte Firmenname, Geschäftsführer und E-Mail ausfüllen');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/register-company`, form);
      setSuccess(true);
      toast.success('Registrierung erfolgreich! Zugangsdaten per E-Mail gesendet.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-slate-900/50 border border-slate-800/60 rounded-sm p-8 text-center">
          <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Registrierung erfolgreich!</h2>
          <p className="text-slate-400 mb-4">Ihre Zugangsdaten wurden an <span className="text-amber-400 font-mono">{form.email}</span> gesendet.</p>
          <p className="text-sm text-slate-500 mb-6">Bitte prüfen Sie Ihr Postfach und melden Sie sich mit dem zugesandten Passwort an.</p>
          <Button onClick={() => navigate('/login')} className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm px-8">
            Zum Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="register-company-page">
      <div className="max-w-md mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-amber-500 rounded-sm flex items-center justify-center">
              <Zap className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
            </div>
            <span className="text-xl font-black text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Mont<span className="text-amber-500">ARO</span>
            </span>
          </div>
          <h1 className="text-3xl font-bold text-white">Kostenlos testen</h1>
          <p className="text-slate-400 mt-2">14 Tage alle Funktionen — keine Kreditkarte nötig</p>
        </div>

        <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-6 space-y-4">
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-sm p-3 text-center">
            <p className="text-sm text-amber-400 font-medium">14 Tage kostenlos testen</p>
            <p className="text-xs text-slate-500 mt-1">Passwort wird per E-Mail zugestellt</p>
          </div>
          <div>
            <Label className="text-slate-300">Firmenname *</Label>
            <Input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})}
              placeholder="Mein Handwerksbetrieb GmbH" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
          </div>
          <div>
            <Label className="text-slate-300">Geschäftsführer *</Label>
            <Input value={form.manager_name} onChange={e => setForm({...form, manager_name: e.target.value})}
              placeholder="Max Mustermann" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
          </div>
          <div>
            <Label className="text-slate-300">E-Mail *</Label>
            <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
              placeholder="info@firma.de" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
          </div>
          <div>
            <Label className="text-slate-300">Adresse</Label>
            <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})}
              placeholder="Musterstr. 1" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">PLZ</Label>
              <Input value={form.zip_code} onChange={e => setForm({...form, zip_code: e.target.value})}
                placeholder="10115" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">Ort</Label>
              <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})}
                placeholder="Berlin" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Telefon</Label>
            <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
              placeholder="030 12345678" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
          </div>
          <Button onClick={handleRegister} disabled={loading} data-testid="register-submit"
            className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)] py-5 text-base">
            {loading ? 'Registrieren...' : 'Kostenlos registrieren'}
          </Button>
          <p className="text-center text-xs text-slate-500">
            Bereits registriert? <button onClick={() => navigate('/login')} className="text-amber-400 hover:underline">Anmelden</button>
          </p>
        </div>
      </div>
    </div>
  );
}
